import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import {
  getConsentStatus,
  type ConsentStatusResponse,
} from '../../../optumus-core/src/api/getConsentStatus';

interface UseConsentStatusOptions {
  enabled?: boolean;
  onSuccess?: (data: ConsentStatusResponse) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to check consent status once (no polling).
 * React Query's cache ensures only one successful call.
 * The cancelled request in dev (StrictMode) is expected and will retry automatically.
 */
export function useConsentStatus(options: UseConsentStatusOptions = {}) {
  const { enabled = true, onSuccess, onError } = options;
  const successCalledRef = useRef(false);
  const errorCalledRef = useRef(false);

  const query = useQuery({
    queryKey: ['consent-status'],
    // Intentionally not forwarding React Query's AbortSignal: in StrictMode the
    // first mount would abort the in-flight request, forcing a second call.
    // The singleton in getConsentStatus already dedupes concurrent callers.
    queryFn: () => getConsentStatus(),
    enabled,
    retry: 2, // Retry cancelled requests (StrictMode causes cancellation)
    retryDelay: 0, // Retry immediately
    staleTime: Infinity, // Never refetch once loaded
    gcTime: Infinity, // Keep in cache forever
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Don't refetch on component remount
    refetchOnReconnect: false,
  });

  // Call success callback once when data is available
  useEffect(() => {
    if (query.isSuccess && query.data && onSuccess && !successCalledRef.current) {
      successCalledRef.current = true;
      onSuccess(query.data);
    }
  }, [query.isSuccess, query.data, onSuccess]);

  // Call error callback once when error occurs
  useEffect(() => {
    if (query.isError && query.error && onError && !errorCalledRef.current) {
      errorCalledRef.current = true;
      onError(query.error as Error);
    }
  }, [query.isError, query.error, onError]);

  return {
    isConsentActive: query.data?.isConsentActive,
    redirectUrl: query.data?.redirectUrl,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    data: query.data,
  };
}
